import Vue from 'vue';
import moment from 'moment-timezone';
import Multiselect from 'vue-multiselect';
import Datepicker from 'vuejs-datepicker';
import Notifications from 'vue-notification';
import VTooltip from 'v-tooltip';
import VueGtm from 'vue-gtm';
import AsyncComputed from 'vue-async-computed';
import VueSocketio from 'vue-socket.io-extended';
import io from 'socket.io-client';
import * as VueGoogleMaps from 'vue2-google-maps';
import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';
import NProgress from 'nprogress';
// import LogRocket from 'logrocket';
import UUID from 'vue-uuid';

import 'vue-awesome/icons/cart-plus';
import 'vue-awesome/icons/dollar-sign';
import 'vue-awesome/icons/money-bill-alt';
import 'vue-awesome/icons/chevron-left';
import 'vue-awesome/icons/times';
import 'vue-awesome/icons/handshake';
import 'vue-awesome/icons/user-plus';
import 'vue-awesome/icons/paper-plane';
import 'vue-awesome/icons/tag';
import 'vue-awesome/icons/regular/arrow-alt-circle-right';
import 'vue-awesome/icons/brands/whatsapp';
import 'vue-awesome/icons/brands/facebook-f';
import 'vue-awesome/icons/brands/slack-hash';
import 'vue-awesome/icons/barcode';
import Icon from 'vue-awesome/components/Icon.vue';
// import FontPickerVue from 'font-picker-vue';

// eslint-disable-next-line
import NodeTree from '@/components/funnels/pagebuilder/nodes/NodeTree.vue';

import App from '@/App.vue';
import router from '@/router';
import store from '@/store';
import { i18n, loadLanguageAsync } from '@/setup/i18n-setup';
import '@/registerServiceWorker';

import {
	getCurrencySymbol,
	formatMoney,
	formatNumberPlain,
	slugify,
	isMobile,
	isMobileOrTablet,
	// getYoutubeId,
	isValidURL,
	isNumber,
	delay,
	isValidEmail,
	isValidSubdomain,
} from '@/lib/helper';

import '@/lib/directives';

// import 'vue-multiselect/dist/vue-multiselect.min.css';
// import './assets/scss/base.scss';

import 'leaflet/dist/leaflet.css';

Vue.config.productionTip = false;

const isProduction = process.env.NODE_ENV === 'production';
const googleMapsApiKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;
const SOCKET_URL = process.env.VUE_APP_SOCKET_URL;

// Socket
const socket = io(SOCKET_URL, {
	transports: ['websocket'],
});
Vue.use(VueSocketio, socket, { store });

// Init 3rd Parties
if (isProduction) {
	// Init Sentry
	Sentry.init({
		dsn: 'https://46fbfda65ce042ad8ca0e0aa9f4c4a32@sentry.io/1360248',
		integrations: [new Integrations.Vue({ Vue, attachProps: true })],
		release: 'orderonline-app-vue@2.0.0',
	});
	// Init LogRocket
	// LogRocket.init('qg41zd/orderonline');
}

moment.locale('id');

Vue.prototype.moment = moment;
Vue.prototype.mediaUrlPrefix = 'https://cdn.ostock.id';
Vue.prototype.$bus = new Vue(); // Global Event Bus

// Vue.use(Vuex);
// Vue.use(VueI18n);
Vue.use(Notifications);
Vue.use(VTooltip);
// Vue.use(FontPickerVue);

Vue.use(UUID);

// GTM
Vue.use(VueGtm, {
	enabled: true, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
	debug: false, // Whether or not display console logs debugs (optional)
	vueRouter: router, // Pass the router instance to automatically sync with router (optional)
	ignoredViews: [], // If router, you can exclude some routes name (case insensitive) (optional)
});

// Google Maps
Vue.use(VueGoogleMaps, {
	load: {
		key: googleMapsApiKey,
		libraries: 'places',
	},
});

// Amplitude
if (window.amplitude) {
	Vue.prototype.$amplitude = window.amplitude.getInstance();
}

// Global Tracking
function track(eventType, eventProperties = {}) {
	if (this.$amplitude) {
		this.$amplitude.logEvent(eventType, eventProperties);
	} else if (window.amplitude) {
		Vue.prototype.$amplitude = window.amplitude.getInstance();
		this.$amplitude.logEvent(eventType, eventProperties);
	}
	// if (window.Intercom) {
	// 	window.Intercom('trackEvent', eventType, eventProperties);
	// }
}
Vue.prototype.$track = track;

// Async Computed
Vue.use(AsyncComputed);

Vue.component('multiselect', Multiselect);
Vue.component('icon', Icon);
Vue.component('datepicker', Datepicker);
Vue.component('NodeTree', NodeTree);

// General Tag
Vue.component('general-tag', {
	render(createElement) {
		const self = this;
		const attrs = {
			on: {
				input(event) {
					const value = event.target.innerText;
					self.$emit('input', value);
				},
				focusout(event) {
					const value = event.target.innerText;
					self.$emit('focusout', value);
				},
			},
		};
		return createElement(
			`${this.tag}`, // tag name
			attrs,
			this.$slots.default, // array of children
		);
	},
	props: {
		tag: {
			type: String,
			required: true,
		},
	},
});

// Click Outside Directive
Vue.directive('click-outside', {
	bind(el, binding, vnode) {
		const vm = vnode.context;
		const callback = binding.value;
		el.clickOutsideEvent = (event) => {
			if (!(el === event.target || el.contains(event.target))) {
				return callback.call(vm, event);
			}
			return false;
		};
		document.body.addEventListener('click', el.clickOutsideEvent);
	},
	unbind(el) {
		document.body.removeEventListener('click', el.clickOutsideEvent);
	},
});

const myMixin = {
	methods: {
		getCurrencySymbol,
		formatMoney,
		formatNumberPlain,
		slugify,
		isMobile,
		isMobileOrTablet,
		// getYoutubeId,
		isValidURL,
		isNumber,
		isValidEmail,
		isValidSubdomain,
		ooDelay: delay,
	},
};

Vue.mixin(myMixin);
// Route Middlewares

// Authorization Check
router.beforeEach((to, from, next) => {
	const path = to.path;
	const isLoggedIn = store.getters.isLoggedIn;
	const user = store.getters.user;
	// const isHasUsername = store.getters.isHasUsername;
	const isHasBankAccounts = store.getters.isHasBankAccounts;
	const isValidIdentityCard = store.getters.isValidIdentityCard;
	const isAdmin = store.getters.isAdmin;

	const errors = [];

	// Check Welcome
	const isNoWelcome = isLoggedIn && isHasBankAccounts && isValidIdentityCard && isAdmin && path === '/welcome';
	if (isNoWelcome) {
		errors.push('welcome');
	}

	// Check Admin
	// const isNotAdmin = path.includes('/admin') && user && !user.is_admin;
	// const isNotSupport = path.includes('/admin') && user && !isSupport(user._id);

	// if (isNotAdmin) {
	// 	errors.push('admin');
	// }

	// if (isNotAdmin && isNotSupport) {
	// 	errors.push('support');
	// }

	// Check Owner Access
	if (isLoggedIn && user && !user.role === 'owner') {
		// Check Access Members
		const isNoAccessMembers = path.includes('/members');
		if (isNoAccessMembers) {
			errors.push('members_view');
		}

		// Check Settings Page Access
		const isNoAccessSettings = path.includes('/settings');
		if (isNoAccessSettings) {
			errors.push('settings_view');
		}
	}

	const isNotAvailable = errors.length > 0;

	const isNeedOnBoarding = isLoggedIn
		&& user
		&& user.role === 'owner'
		&& (!isHasBankAccounts || !isValidIdentityCard)
		&& !isAdmin
		&& to.path !== '/welcome';

	if (
		to.path !== '/'
		&& to.path !== '/login'
		&& to.path !== '/admin/login'
		&& to.path !== '/register'
		&& to.path !== '/register/supplier'
		&& to.path !== '/search'
		&& !to.path.includes('/page')
		&& to.path !== '/index.html'
		&& to.path !== '/signout'
		&& to.path !== '/password/reset'
		&& to.path !== '/auth/callback'
		&& to.path !== '/billings-redirect'
		&& to.name !== 'product-detail'
		&& to.path !== '/brand'
		&& to.path !== '/direct'
		&& !to.path.includes('/supplier')
		&& !isLoggedIn
	) {
		const currentPath = to.path;
		if (currentPath.includes('admin')) {
			next(`/admin/login?ref=${currentPath}`);
		} else {
			next(`/login?ref=${currentPath}`);
		}
	} else if (to.path === '/login' && isLoggedIn) {
		next('/');
	} else if (isNeedOnBoarding) {
		next('/welcome');
	} else if (isNotAvailable) {
		next('/');
	} else {
		document.title = to.meta.title;
		next();
	}
});

// Save Current Nav to Global Store
router.beforeEach((to, from, next) => {
	store.dispatch('navigate', { to });
	next();
});

// Lazy load Languages
router.beforeEach((to, from, next) => {
	const lang = store.getters.locale;
	loadLanguageAsync(lang).then(() => next());
});

router.beforeResolve((to, from, next) => {
	if (to.name) {
		NProgress.start();
	}
	next();
});

router.afterEach(() => {
	NProgress.done();
});

Vue.config.errorHandler = (err, vm, info) => {
	// Check if the error is a ChunkLoadError
	if (err.message.match(/ChunkLoadError/)) {
		// Reload the application
		window.location.reload();
	}

	// You can also log the error or perform other actions here
	console.error(err, info);
};

/* eslint-disable no-new */
new Vue({
	store,
	router,
	i18n,
	render: h => h(App),
}).$mount('#app');
